import GenericApi from "@/api/genericApi";
import {
  BalanceQueryKey,
  PendingBalanceQueryKey,
} from "@/config/constants/QueryKeys";
import { ApiRoutes } from "@/config/routes/ApiRoutes";
import { ApiResponse } from "@/types/Api";
import { Balance } from "@/types/Balance";
import { isBitramp } from "@/utils/utils";
import { useQuery } from "@tanstack/react-query";
import { Loader } from "lucide-react";
import React from "react";

const groupBalances = (arr1: Balance[], arr2: Balance[]): Balance[][] => {
  const grouped: Record<string, [Balance | null, Balance | null]> = {};

  arr1.forEach((item) => {
    grouped[item.currency] = grouped[item.currency] || [null, null];
    grouped[item.currency][0] = item;
  });

  arr2.forEach((item) => {
    grouped[item.currency] = grouped[item.currency] || [null, null];
    grouped[item.currency][1] = item;
  });

  return Object.entries(grouped).map(([currency, pair]) => [
    pair[0] || { currency, balance: "0" },
    pair[1] || { currency, balance: "0" },
  ]);
};

const ReportsBalance: React.FC = () => {
  const { data: balance, isLoading: isLoadingBalance } = useQuery<
    ApiResponse<Balance[]>
  >({
    queryFn: async () => GenericApi.get<Balance[]>(ApiRoutes.GET_BALANCE),
    queryKey: [BalanceQueryKey],
  });

  const { data: pendingBalance, isLoading: isLoadingPendingBalance } = useQuery<
    ApiResponse<Balance[]>
  >({
    queryFn: async () =>
      GenericApi.get<Balance[]>(ApiRoutes.GET_BALANCE_PENDING),
    queryKey: [PendingBalanceQueryKey],
  });

  if (isLoadingBalance || isLoadingPendingBalance) {
    return <Loader />;
  }

  const groupedData = groupBalances(
    balance?.data || [],
    pendingBalance?.data || []
  );

  return (
    <div>
      <div className="flex flex-col gap-4">
        {groupedData.map(([item1, item2]) => {
          const totalBalance = Number(item1.balance) + Number(item2.balance);
          return (
            <div
              key={item1.currency}
              className="rounded flex flex-col items-center"
            >
              <div className={`${isBitramp() ? "text-white" : ""} font-bold`}>
                {item1.currency} - Total: {totalBalance}
              </div>
              <div className={`${isBitramp() ? "border-[#464646]" : ""} w-full rounded-lg border`}>
                <div
                  className={`bg-[#E9E9E9] w-full h-4 rounded-t-lg relative`}
                >
                  <div
                    className={`bg-[#222B45] h-4 rounded-tl-lg ${
                      isBitramp() ? "bg-[#EE33D9]" : ""
                    }`}
                    style={{
                      width: `${(Number(item1.balance) / totalBalance) * 100}%`,
                    }}
                  ></div>
                </div>
                <div
                  className={`flex justify-between w-full p-4 ${
                    isBitramp() ? "text-[#9F9F9F]" : ""
                  }`}
                >
                  <div>Balance: {Number(item1.balance)}</div>
                  <div>Pending: {Number(item2.balance)}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ReportsBalance;
